import { useMemo } from "react";
import { toast } from "sonner";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Loader2 } from "lucide-react";
import { useActivateAiPromptVersion } from "@/resources/ai-prompt";
import type { AiPromptVersion } from "@/types/ai-prompt";
import { handleApiError } from "@/utils";
import { formatVersionDate, computeLineDiff } from "./utils";
import type { DiffEntry } from "./types";
import { TruncatedText } from "@/components/ui/truncated-text";

type DiffLineProps = {
  type: "added" | "removed" | "context";
  children: React.ReactNode;
};

function DiffLine({ type, children }: DiffLineProps) {
  const className =
    type === "added"
      ? "block bg-green-500/10 text-foreground px-2 -mx-2 rounded-sm"
      : type === "removed"
      ? "block bg-red-500/10 text-foreground px-2 -mx-2 rounded-sm"
      : "block text-muted-foreground px-2 -mx-2";
  return <span className={className}>{children || "\u00A0"}{"\n"}</span>;
}

export type VersionDiffModalProps = {
  open: boolean;
  onClose: () => void;
  activeVersionText: string;
  pastVersion: AiPromptVersion | null;
  promptLabel: string;
  promptId: number;
};

export function VersionDiffModal({ open, onClose, activeVersionText, pastVersion, promptLabel, promptId }: VersionDiffModalProps) {
  const pastBody = pastVersion?.prompt_text ?? "";

  const diff = useMemo(() => computeLineDiff(pastBody, activeVersionText), [pastBody, activeVersionText]);

  const { mutate: activateVersion, isPending: isRestoring } = useActivateAiPromptVersion();

  const handleRestore = () => {
    if (!pastVersion) return;
    activateVersion(
      { promptId, versionId: pastVersion.id },
      {
        onSuccess: () => {
          toast.success("Version restored");
          onClose();
        },
        onError: (error) => handleApiError(error),
      }
    );
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-[95vw] w-[95vw] h-[90vh] p-0 gap-0 flex flex-col">
        <DialogHeader className="px-6 py-4 border-b border-border space-y-1 shrink-0">
          <DialogTitle className="text-lg font-semibold text-foreground">
            Version diff — {promptLabel}
          </DialogTitle>
          <DialogDescription className="text-xs text-muted-foreground">
            Comparing the live version against the selected past version. Added lines are highlighted green, removed lines red.
          </DialogDescription>
        </DialogHeader>

        <div className="grid grid-cols-2 flex-1 min-h-0">
          <div className="flex flex-col min-h-0 border-r border-border">
            <div className="px-5 py-3 border-b border-border bg-muted/30 shrink-0">
              <div className="flex items-center gap-2 mb-0.5">
                <p className="text-sm font-semibold text-foreground min-w-0">
                  <TruncatedText>{pastVersion?.name ?? "Past version"}</TruncatedText>
                </p>
                <Badge variant="outline" className="text-xs">Past</Badge>
              </div>
              {pastVersion && (
                <p className="text-xs text-muted-foreground">
                  {formatVersionDate(pastVersion.created_at)}
                </p>
              )}
            </div>
            <div className="flex-1 min-h-0 overflow-auto">
              <pre className="text-sm leading-relaxed font-sans whitespace-pre-wrap m-0 px-5 py-4">
                {diff.map((d: DiffEntry, i: number) =>
                  d.side === "left" ? (
                    <DiffLine key={i} type={d.type}>{d.text}</DiffLine>
                  ) : null
                )}
              </pre>
            </div>
          </div>

          <div className="flex flex-col min-h-0">
            <div className="px-5 py-3 border-b border-border bg-muted/30 shrink-0">
              <div className="flex items-center gap-2 mb-0.5">
                <p className="text-sm font-semibold text-foreground min-w-0">
                  <TruncatedText>Current version</TruncatedText>
                </p>
                <Badge variant="secondary" className="text-xs">Live</Badge>
              </div>
            </div>
            <div className="flex-1 min-h-0 overflow-auto">
              <pre className="text-sm leading-relaxed font-sans whitespace-pre-wrap m-0 px-5 py-4">
                {diff.map((d: DiffEntry, i: number) =>
                  d.side === "right" ? (
                    <DiffLine key={i} type={d.type}>{d.text}</DiffLine>
                  ) : null
                )}
              </pre>
            </div>
          </div>
        </div>

        <div className="px-6 py-3 border-t border-border flex items-center gap-2 shrink-0">
          <Button variant="outline" size="sm" onClick={onClose}>Close</Button>
          <Button
            size="sm"
            onClick={handleRestore}
            disabled={isRestoring || pastVersion?.is_active === true}
          >
            {isRestoring && <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />}
            Restore this version
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
